export const content = {
  en: {
    nav: {
      contactWhatsApp: "Contact on WhatsApp",
      learnMore: "Learn How It Works"
    },
    hero: {
      headline: "Secure Your Child's Future in Poland",
      subheadline: "Full admission, visa, and relocation support — your child is never left alone.",
      ctaPrimary: "Contact us on WhatsApp",
      ctaSecondary: "Learn how it works"
    },
    trust: {
      headline: "Why Families Choose Support Instead of Risk",
      problems: [
        {
          title: "Admission Mistakes",
          description: "Wrong documents or missed deadlines can cost an entire academic year"
        },
        {
          title: "Visa Rejection",
          description: "One mistake in paperwork means starting over and losing valuable time"
        },
        {
          title: "Child Alone Abroad",
          description: "Navigating a new country without local guidance is overwhelming"
        },
        {
          title: "Paperwork Confusion",
          description: "Complex requirements in Polish and English create stress and errors"
        }
      ],
      solution: "Marhaba Poland eliminates these risks with structured processes, local presence, and full guidance at every step."
    },
    about: {
      headline: "Why Families Choose Marhaba Poland",
      subheadline: "The Only Arabic-Speaking Agency Officially Registered in Poland",
      features: [
        {
          title: "4+ Years Experience",
          description: "Proven track record helping families achieve their educational dreams"
        },
        {
          title: "~50 Students Annually",
          description: "Quality over quantity - personalized attention for each family"
        },
        {
          title: "MENA Region Focus",
          description: "Deep understanding of your culture, concerns, and needs"
        },
        {
          title: "Medicine Specialists",
          description: "Expert guidance for competitive medical programs"
        },
        {
          title: "Based in Poland",
          description: "Real local presence, not remote support"
        },
        {
          title: "University Trusted",
          description: "Regularly invited by universities, participating in international education conferences"
        }
      ]
    },
    process: {
      headline: "How We Guide Your Child – Step by Step",
      successBadge: "100% Admission & Visa Success (Last Year)",
      steps: [
        {
          number: "01",
          title: "Planning",
          description: "We understand your goals, evaluate options, and create a personalized plan"
        },
        {
          number: "02",
          title: "Preparation",
          description: "Document collection, translation, and verification with expert guidance"
        },
        {
          number: "03",
          title: "Applications",
          description: "Professional submission to top universities with proven processes"
        },
        {
          number: "04",
          title: "Admission",
          description: "Securing your child's place at the right university and program"
        },
        {
          number: "05",
          title: "Visa",
          description: "Complete visa support ensuring approval with proper documentation"
        },
        {
          number: "06",
          title: "Arrival",
          description: "Airport pickup, accommodation, registration, and settling in Poland"
        }
      ]
    },
    packages: {
      headline: "Choose Your Support Package",
      subheadline: "Most families choose Concierge for complete peace of mind",
      packages: [
        {
          name: "Essential",
          price: "Contact for pricing",
          popular: false,
          description: "Basic admission and visa support",
          features: [
            "University selection guidance",
            "Application submission",
            "Visa document preparation",
            "Email support",
            "Admission confirmation"
          ]
        },
        {
          name: "Concierge",
          price: "Contact for pricing",
          popular: true,
          description: "Full support from start to settlement",
          features: [
            "Everything in Essential",
            "Airport pickup",
            "Accommodation assistance",
            "Temporary Residence Card (TRC) support",
            "Regular parent updates",
            "First month local guidance",
            "Bank account setup",
            "Mobile registration"
          ]
        },
        {
          name: "Concierge+",
          price: "Contact for pricing",
          popular: false,
          description: "Extended support for complete adaptation",
          features: [
            "Everything in Concierge",
            "Extended adaptation support (3 months)",
            "Weekly student check-ins",
            "Academic progress monitoring",
            "Emergency 24/7 support",
            "Cultural adaptation guidance",
            "Parent video updates"
          ]
        }
      ],
      note: "Most families choose Concierge package"
    },
    safety: {
      headline: "Your Child Is Not Alone in Poland",
      description: "We don't just help with paperwork — we provide real, local support that makes Poland feel like home.",
      supports: [
        {
          title: "Local Presence",
          description: "Our team is physically in Poland, available when needed"
        },
        {
          title: "After Arrival Support",
          description: "Continued guidance beyond visa approval and arrival"
        },
        {
          title: "Real-Life Help",
          description: "From doctor visits to university questions, we're here"
        },
        {
          title: "Arabic Communication",
          description: "No language barriers when you need help most"
        }
      ]
    },
    faq: {
      headline: "Questions Parents Ask Us",
      questions: [
        {
          question: "Can my child work while studying in Poland?",
          answer: "Yes. International students can work without any permits or additional documents. There is no limit to the amount of hours they can work, which helps significantly with living expenses."
        },
        {
          question: "Are classes taught in English?",
          answer: "Yes. We specialize in English-taught programs, mainly in medicine and engineering. No Polish language requirement for these programs."
        },
        {
          question: "Do you help find accommodation?",
          answer: "Yes. We help arrange university dormitories or private apartments, depending on the student's preference and budget."
        },
        {
          question: "What if my child fails an entrance exam?",
          answer: "We prepare students thoroughly and help select realistic programs. If needed, we guide you through reapplication or alternative program options to ensure your child's educational goals are met."
        },
        {
          question: "What programs do you specialize in?",
          answer: "We specialize primarily in medicine and engineering programs. We also assist with dentistry, pharmacy, veterinary medicine, and other competitive programs across Poland's top universities."
        },
        {
          question: "Do I need to travel to Poland for the process?",
          answer: "No, you don't need to travel during the application and visa process. We handle everything remotely. However, parents are welcome to visit during the process or after arrival, and we can help arrange that."
        },
        {
          question: "How do you communicate with parents?",
          answer: "We provide regular updates via WhatsApp, phone calls, and video calls in Arabic or English. Our Concierge packages include scheduled parent updates throughout the entire process and beyond."
        },
        {
          question: "Are Polish medical degrees recognized internationally?",
          answer: "Yes. Polish medical degrees are recognized by the WHO, EU, and most countries worldwide including the USA (USMLE), UK (GMC), and Middle Eastern countries. Graduates can practice medicine internationally."
        },
        {
          question: "What makes you different from other agencies?",
          answer: "We're the only Arabic-speaking agency officially registered in Poland. We have physical presence in Poland, university partnerships, and participate in international education conferences. You're working with trusted local experts, not remote coordinators."
        },
        {
          question: "How many students do you accept per year?",
          answer: "We work with approximately 50 students annually. We prioritize quality over quantity, ensuring each family receives personalized attention and dedicated support throughout their journey."
        },
        {
          question: "Is there a guarantee of admission?",
          answer: "While we cannot guarantee admission (as universities make final decisions), we had 100% admission success last year. We carefully assess each student's qualifications and only proceed when we're confident in achieving success."
        },
        {
          question: "What if my child gets rejected?",
          answer: "Rejection is extremely rare with proper preparation. If it happens, we analyze the reasons, guide you through appeals if applicable, or help reapply with strengthened applications. We remain committed until your child secures admission."
        },
        {
          question: "What if something goes wrong in Poland?",
          answer: "Our local presence means we're always available to help. From medical emergencies to academic concerns or daily life challenges, we provide real-time support. Our Concierge+ package includes 24/7 emergency support."
        },
        {
          question: "What if the visa application is rejected?",
          answer: "With proper preparation, rejection is extremely rare. We had 100% visa success last year. If issues arise, we guide you through the appeal process or reapplication with corrected documentation."
        },
        {
          question: "How long does the entire process take?",
          answer: "From initial planning to arrival in Poland typically takes 4-6 months. We recommend starting 6-8 months before the intended semester to ensure comfortable timing."
        },
        {
          question: "What are the monthly living costs?",
          answer: "Students typically spend €400-600/month including accommodation, food, transport, and basic expenses. This is significantly lower than Western European countries."
        },
        {
          question: "Is Poland safe for international students?",
          answer: "Poland is one of the safest countries in Europe with low crime rates. Polish cities have large international student communities, and universities provide additional safety resources."
        }
      ]
    },
    cta: {
      headline: "Start the Process Safely",
      subheadline: "Connect with us on WhatsApp for a free consultation. We typically respond within 2 hours.",
      ctaWhatsApp: "Chat on WhatsApp",
      ctaMessenger: "Message on Facebook",
      note: "Average response time: 2 hours during business days"
    },
    socialProof: {
      headline: "Trusted by Families Across MENA",
      testimonials: [
        "Families from Egypt trust Marhaba Poland",
        "Students from Saudi Arabia study with our support",
        "UAE parents choose our guidance",
        "Trusted partner for Qatari families"
      ]
    },
    footer: {
      tagline: "Marhaba Poland — Your trusted partner for education in Poland",
      contact: "Contact Us",
      whatsappArabic: "WhatsApp (Arabic)",
      whatsappEnglish: "WhatsApp (English)",
      social: "Follow Us",
      rights: "© 2025 Marhaba Poland. All rights reserved."
    }
  },
  ar: {
    nav: {
      contactWhatsApp: "تواصل عبر واتساب",
      learnMore: "كيف نعمل"
    },
    hero: {
      headline: "احمِ مستقبل ابنك في بولندا",
      subheadline: "دعم كامل للقبول والفيزا والانتقال — ابنك لن يكون وحيدًا أبدًا",
      ctaPrimary: "تواصل معنا على واتساب",
      ctaSecondary: "اعرف كيف نعمل"
    },
    trust: {
      headline: "لماذا تختار العائلات الدعم بدلاً من المخاطرة",
      problems: [
        {
          title: "أخطاء القبول",
          description: "وثائق خاطئة أو مواعيد فائتة قد تكلفك عامًا دراسيًا كاملاً"
        },
        {
          title: "رفض الفيزا",
          description: "خطأ واحد في الأوراق يعني البدء من جديد وخسارة وقت ثمين"
        },
        {
          title: "ابنك وحيدًا بالخارج",
          description: "التنقل في بلد جديد دون إرشاد محلي أمر صعب جدًا"
        },
        {
          title: "تعقيد الأوراق",
          description: "متطلبات معقدة بالبولندية والإنجليزية تسبب توترًا وأخطاء"
        }
      ],
      solution: "مرحبا بولندا تزيل هذه المخاطر بعمليات منظمة، تواجد محلي، وإرشاد كامل في كل خطوة"
    },
    about: {
      headline: "لماذا تختار العائلات مرحبا بولندا",
      subheadline: "الوكالة العربية الوحيدة المسجلة رسميًا في بولندا",
      features: [
        {
          title: "خبرة أكثر من 4 سنوات",
          description: "سجل حافل في مساعدة العائلات لتحقيق أحلامهم التعليمية"
        },
        {
          title: "~50 طالب سنويًا",
          description: "الجودة قبل الكمية - اهتمام شخصي لكل عائلة"
        },
        {
          title: "تركيز على منطقة MENA",
          description: "فهم عميق لثقافتك ومخاوفك واحتياجاتك"
        },
        {
          title: "متخصصون في الطب",
          description: "إرشاد خبير للبرامج الطبية التنافسية"
        },
        {
          title: "مقرنا في بولندا",
          description: "تواجد محلي حقيقي، ليس دعمًا عن بُعد"
        },
        {
          title: "موثوقون من الجامعات",
          description: "ندعى بانتظام من الجامعات، ونشارك في مؤتمرات التعليم الدولية"
        }
      ]
    },
    process: {
      headline: "كيف نرشد ابنك - خطوة بخطوة",
      successBadge: "نسبة نجاح 100% للقبول والفيزا (العام الماضي)",
      steps: [
        {
          number: "01",
          title: "التخطيط",
          description: "نفهم أهدافك، نقيّم الخيارات، ونضع خطة شخصية"
        },
        {
          number: "02",
          title: "التحضير",
          description: "جمع الوثائق، الترجمة، والتحقق بإرشاد خبير"
        },
        {
          number: "03",
          title: "التقديم",
          description: "تقديم احترافي لأفضل الجامعات بعمليات مثبتة"
        },
        {
          number: "04",
          title: "القبول",
          description: "تأمين مكان ابنك في الجامعة والبرنامج المناسب"
        },
        {
          number: "05",
          title: "الفيزا",
          description: "دعم كامل للفيزا لضمان الموافقة بوثائق صحيحة"
        },
        {
          number: "06",
          title: "الوصول",
          description: "استقبال من المطار، السكن، التسجيل، والاستقرار في بولندا"
        }
      ]
    },
    packages: {
      headline: "اختر باقة الدعم المناسبة",
      subheadline: "معظم العائلات تختار باقة Concierge لراحة بال كاملة",
      packages: [
        {
          name: "أساسية",
          price: "تواصل للأسعار",
          popular: false,
          description: "دعم القبول والفيزا الأساسي",
          features: [
            "إرشاد اختيار الجامعة",
            "تقديم الطلبات",
            "تحضير وثائق الفيزا",
            "دعم عبر البريد",
            "تأكيد القبول"
          ]
        },
        {
          name: "Concierge",
          price: "تواصل للأسعار",
          popular: true,
          description: "دعم كامل من البداية للاستقرار",
          features: [
            "كل ما في الباقة الأساسية",
            "استقبال من المطار",
            "مساعدة السكن",
            "دعم بطاقة الإقامة المؤقتة",
            "تحديثات منتظمة للأهل",
            "إرشاد محلي الشهر الأول",
            "فتح حساب بنكي",
            "تسجيل الهاتف"
          ]
        },
        {
          name: "Concierge+",
          price: "تواصل للأسعار",
          popular: false,
          description: "دعم ممتد للتأقلم الكامل",
          features: [
            "كل ما في Concierge",
            "دعم تأقلم ممتد (3 أشهر)",
            "متابعة أسبوعية للطالب",
            "مراقبة التقدم الأكاديمي",
            "دعم طوارئ 24/7",
            "إرشاد التأقلم الثقافي",
            "تحديثات فيديو للأهل"
          ]
        }
      ],
      note: "معظم العائلات تختار باقة Concierge"
    },
    safety: {
      headline: "ابنك ليس وحيدًا في بولندا",
      description: "لا نساعد فقط بالأوراق — نقدم دعمًا محليًا حقيقيًا يجعل بولندا تشعر كالبيت",
      supports: [
        {
          title: "تواجد محلي",
          description: "فريقنا موجود فعليًا في بولندا، متاح عند الحاجة"
        },
        {
          title: "دعم بعد الوصول",
          description: "إرشاد مستمر بعد موافقة الفيزا والوصول"
        },
        {
          title: "مساعدة حقيقية",
          description: "من زيارات الطبيب إلى أسئلة الجامعة، نحن هنا"
        },
        {
          title: "تواصل بالعربية",
          description: "لا حواجز لغوية عندما تحتاج المساعدة"
        }
      ]
    },
    faq: {
      headline: "أسئلة يطرحها الأهالي",
      questions: [
        {
          question: "هل يمكن لابني العمل أثناء الدراسة في بولندا؟",
          answer: "نعم. الطلاب الدوليون يمكنهم العمل دون أي تصاريح أو وثائق إضافية. لا يوجد حد لعدد ساعات العمل، مما يساعد بشكل كبير في تغطية نفقات المعيشة."
        },
        {
          question: "هل الدراسة بالإنجليزية؟",
          answer: "نعم. نحن متخصصون في البرامج الدراسية باللغة الإنجليزية، خاصة في الطب والهندسة. لا حاجة للغة البولندية لهذه البرامج."
        },
        {
          question: "هل تساعدون بإيجاد السكن؟",
          answer: "نعم. نساعد بترتيب السكن الجامعي أو الشقق الخاصة، حسب تفضيلات الطالب والميزانية."
        },
        {
          question: "ماذا لو فشل ابني بامتحان القبول؟",
          answer: "نحضر الطلاب جيدًا ونساعد باختيار برامج واقعية. إذا لزم الأمر، نرشدك خلال إعادة التقديم أو خيارات برامج بديلة لضمان تحقيق أهداف ابنك التعليمية."
        },
        {
          question: "ما هي البرامج التي تتخصصون بها؟",
          answer: "نتخصص بشكل أساسي في برامج الطب والهندسة. كما نساعد في طب الأسنان، الصيدلة، الطب البيطري، وبرامج تنافسية أخرى في أفضل جامعات بولندا."
        },
        {
          question: "هل أحتاج للسفر إلى بولندا خلال العملية؟",
          answer: "لا، لا حاجة للسفر خلال عملية التقديم والفيزا. نتولى كل شيء عن بُعد. لكن الأهل مرحب بهم للزيارة خلال العملية أو بعد الوصول، ويمكننا المساعدة في ترتيب ذلك."
        },
        {
          question: "كيف تتواصلون مع الأهل؟",
          answer: "نقدم تحديثات منتظمة عبر واتساب، مكالمات هاتفية، ومكالمات فيديو بالعربية أو الإنجليزية. باقات Concierge تشمل تحديثات مجدولة للأهل طوال العملية وما بعدها."
        },
        {
          question: "هل الشهادات الطبية البولندية معترف بها دوليًا؟",
          answer: "نعم. الشهادات الطبية البولندية معترف بها من منظمة الصحة العالمية، الاتحاد الأوروبي، ومعظم دول العالم بما فيها أمريكا (USMLE)، بريطانيا (GMC)، ودول الشرق الأوسط. الخريجون يمكنهم ممارسة الطب دوليًا."
        },
        {
          question: "ما الذي يميزكم عن الوكالات الأخرى؟",
          answer: "نحن الوكالة العربية الوحيدة المسجلة رسميًا في بولندا. لدينا تواجد فعلي في بولندا، شراكات جامعية، ونشارك في مؤتمرات التعليم الدولية. تتعاملون مع خبراء محليين موثوقين، وليس منسقين عن بُعد."
        },
        {
          question: "كم عدد الطلاب الذين تقبلونهم سنويًا؟",
          answer: "نعمل مع حوالي 50 طالبًا سنويًا. نعطي الأولوية للجودة على الكمية، لضمان حصول كل عائلة على اهتمام شخصي ودعم مخصص طوال رحلتهم."
        },
        {
          question: "هل هناك ضمان للقبول؟",
          answer: "بينما لا يمكننا ضمان القبول (لأن الجامعات تتخذ القرارات النهائية)، كان لدينا نجاح 100% بالقبول العام الماضي. نقيّم مؤهلات كل طالب بعناية ونستمر فقط عندما نكون واثقين من النجاح."
        },
        {
          question: "ماذا لو رُفض ابني؟",
          answer: "الرفض نادر جدًا مع التحضير المناسب. إذا حدث، نحلل الأسباب، نرشدك خلال الاستئناف إن أمكن، أو نساعد بإعادة التقديم بطلبات معززة. نبقى ملتزمين حتى يؤمّن ابنك القبول."
        },
        {
          question: "ماذا لو حدث خطأ ما في بولندا؟",
          answer: "تواجدنا المحلي يعني أننا متاحون دائمًا للمساعدة. من حالات الطوارئ الطبية إلى المخاوف الأكاديمية أو تحديات الحياة اليومية، نقدم دعمًا فوريًا. باقة Concierge+ تشمل دعم طوارئ 24/7."
        },
        {
          question: "ماذا لو رُفض طلب الفيزا؟",
          answer: "مع التحضير الصحيح، الرفض نادر جدًا. كان لدينا نجاح 100% بالفيزا العام الماضي. إذا ظهرت مشاكل، نرشدك خلال الاستئناف أو إعادة التقديم بوثائق مصححة."
        },
        {
          question: "كم تستغرق العملية الكاملة؟",
          answer: "من التخطيط الأولي للوصول لبولندا عادة 4-6 أشهر. ننصح بالبدء قبل 6-8 أشهر من الفصل الدراسي المطلوب لضمان وقت مريح."
        },
        {
          question: "ما هي تكاليف المعيشة الشهرية؟",
          answer: "الطلاب عادة ينفقون 400-600 يورو شهريًا شاملة السكن والطعام والمواصلات والنفقات الأساسية. هذا أقل بكثير من دول أوروبا الغربية."
        },
        {
          question: "هل بولندا آمنة للطلاب الدوليين؟",
          answer: "بولندا من أكثر دول أوروبا أمانًا بمعدلات جريمة منخفضة. المدن البولندية فيها مجتمعات طلابية دولية كبيرة، والجامعات توفر موارد أمان إضافية."
        }
      ]
    },
    cta: {
      headline: "ابدأ العملية بأمان",
      subheadline: "تواصل معنا على واتساب لاستشارة مجانية. عادة نرد خلال ساعتين",
      ctaWhatsApp: "دردشة على واتساب",
      ctaMessenger: "رسالة على فيسبوك",
      note: "متوسط وقت الرد: ساعتين خلال أيام العمل"
    },
    socialProof: {
      headline: "موثوقون من عائلات عبر منطقة MENA",
      testimonials: [
        "عائلات من مصر تثق بمرحبا بولندا",
        "طلاب من السعودية يدرسون بدعمنا",
        "أهالي الإمارات يختارون إرشادنا",
        "شريك موثوق للعائلات القطرية"
      ]
    },
    footer: {
      tagline: "مرحبا بولندا — شريكك الموثوق للتعليم في بولندا",
      contact: "تواصل معنا",
      whatsappArabic: "واتساب (عربي)",
      whatsappEnglish: "واتساب (إنجليزي)",
      social: "تابعنا",
      rights: "© 2025 مرحبا بولندا. جميع الحقوق محفوظة"
    }
  }
};

export const tiktokVideos = [
  {
    id: 1,
    thumbnail: "https://customer-assets.emergentagent.com/job_admission-guided/artifacts/j6l3isxn_DSC05755-2.jpg",
    title: "Student Success Story",
    views: "15K"
  },
  {
    id: 2,
    thumbnail: "https://customer-assets.emergentagent.com/job_admission-guided/artifacts/hzrd2f30_DSC06083-2.jpg",
    title: "University Campus Tour",
    views: "22K"
  },
  {
    id: 3,
    thumbnail: "https://customer-assets.emergentagent.com/job_admission-guided/artifacts/3x7k4yf0_DSC06254-2.jpg",
    title: "Admission Process Guide",
    views: "18K"
  },
  {
    id: 4,
    thumbnail: "https://customer-assets.emergentagent.com/job_admission-guided/artifacts/hifhkohj_DSC05235.jpg",
    title: "Student Life in Poland",
    views: "31K"
  }
];

export const whatsappNumbers = {
  arabic: "+48606247441",
  english: "+48510555499"
};
